import { RestClient } from '@/api/rest-client'
import { Company } from '@/api/model/company'
import { CustomerPortalBranding } from '@/api/model/customer-portal-branding'
import { ReferralNode } from '@/api/model/referral-node'
import { ActiveCampaignCredentials } from '@/api/model/credentials'
import { MailchimpCredentials } from '@/api/model/credentials'
import { TypeformCredentials } from '@/api/model/credentials'
import { User } from '@/api/model/user'
import { AutoCloseSettings } from '@/api/model/auto-close-settings'
import { OrderSettings } from '@/api/model/order-settings'
import { cloneDeep } from 'lodash'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import { ref } from '@vue/composition-api'

export class CompanyClient extends RestClient<Company> {
    collectionName = 'companies'
    parseData = Company.fromData
    currentCompany: Company | null = null
    reactiveCurrentCompany = ref<Company>(null)

    constructor() {
        super()

        if (this.decodedAccessToken) {
            this.get(this.decodedAccessToken.companyId).then(company => {
                this.currentCompany = company
                this.reactiveCurrentCompany.value = company
            })
        }
    }

    async update(id: string, data: Company): Promise<Company> {
        const request = cloneDeep<any>(data)

        delete request.branches
        delete request.incomingReferrals
        delete request.outgoingReferrals
        delete request.credentials
        delete request.users
        delete request.shares

        if (data.customerPortalBranding instanceof CustomerPortalBranding) {
            request.customerPortalBranding = data.customerPortalBranding.resourceIdentifier
        }

        return super.update(id, request)
    }

    async getCurrentCompany(): Promise<Company> {
        if (!this.currentCompany && this.isAuthenticated) {
            this.currentCompany = await this.get(this.decodedAccessToken.companyId)
            this.reactiveCurrentCompany.value = this.currentCompany
        }

        return this.currentCompany
    }

    async reloadCurrentCompany(): Promise<Company> {
        if (this.isAuthenticated) {
            this.currentCompany = await this.get(this.decodedAccessToken.companyId, true)
            this.reactiveCurrentCompany.value = this.currentCompany
        }

        return this.currentCompany
    }

    async getIncomingReferrals(): Promise<ReferralNode[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/incoming_referrals`)
            return Promise.all(response.data['hydra:member'].map(async referral => ReferralNode.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getOutgoingReferrals(): Promise<ReferralNode[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/outgoing_referrals`)
            return Promise.all(response.data['hydra:member'].map(async referral => ReferralNode.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getIncomingConnections(): Promise<PipelineConnection[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/incoming_connections`)
            return Promise.all(response.data['hydra:member'].map(async referral => PipelineConnection.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getOutgoingConnections(): Promise<PipelineConnection[]> {
        if (!this.decodedAccessToken) {
            return []
        }

        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${this.decodedAccessToken.companyId}/outgoing_connections`)
            return Promise.all(response.data['hydra:member'].map(async referral => PipelineConnection.fromData(referral)))

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateWidgetSettings(id: string, data: Company): Promise<Company> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/widget_settings`, {
                widgetEnabled: data.widgetEnabled,
                widgetType: data.widgetType,
                widgetFlowTypes: data.widgetFlowTypes,
                widgetGoals: data.widgetGoals,
                widgetPopupType: data.widgetPopupType,
                widgetPopupFlowType: data.widgetPopupFlowType,
                widgetPopupPosition: data.widgetPopupPosition,
                widgetPopupAnimation: data.widgetPopupAnimation,
                widgetPopupBackgroundColor: data.widgetPopupBackgroundColor,
                widgetPopupTextColor: data.widgetPopupTextColor,
                widgetPopupButtonColor: data.widgetPopupButtonColor,
                widgetPopupButtonTextColor: data.widgetPopupButtonTextColor,
                widgetPopupFont: data.widgetPopupFont,
                widgetPopupContents: data.widgetPopupContents,
                widgetAvatarBackgroundColor: data.widgetAvatarBackgroundColor,
                widgetAvatarTextColor: data.widgetAvatarTextColor,
                widgetInformationTopics: data.widgetInformationTopics,
                widgetChatTextBackground: data.widgetChatTextBackground,
                widgetChatText: data.widgetChatText,
                widgetChatUserText: data.widgetChatUserText,
                widgetChatUserTextBorder: data.widgetChatUserTextBorder,
                widgetChatInputBorder: data.widgetChatInputBorder,
                widgetChatInputText: data.widgetChatInputText,
                widgetChatInputButtonActive: data.widgetChatInputButtonActive,
                widgetChatInputButtonInactive: data.widgetChatInputButtonInactive,
                widgetChatInputButton: data.widgetChatInputButton,
                widgetChatInputButtonText: data.widgetChatInputButtonText,
                widgetChatInputMultiText: data.widgetChatInputMultiText,
                widgetChatInputMultiBorder: data.widgetChatInputMultiBorder,
                widgetChatInputMultiSelectedText: data.widgetChatInputMultiSelectedText,
                widgetChatInputMultiSelectedBorder: data.widgetChatInputMultiSelectedBorder,
                widgetChatInputButtonActiveText: data.widgetChatInputButtonActiveText,
                widgetChatInputButtonInactiveText: data.widgetChatInputButtonInactiveText,
                widgetAvatarCircle: data.widgetAvatarCircle,
                widgetAvatarStyle: data.widgetAvatarStyle,
                widgetPopupOpenTime: data.widgetPopupOpenTime,
            })
            return data
        } catch (error) {
            this.handleError(error)
        }
    }

    async updateActiveCampaignCredentials(id: string, data: ActiveCampaignCredentials): Promise<void> {
        await this.client.put(`${this.basePath}/${this.collectionName}/${id}/active_campaign_credentials`, {
            includeInternalContacts: data.includeInternalContacts,
            includeExternalContacts: data.includeExternalContacts,
            includeRealworksRelaties: data.includeRealworksRelaties,
            syncedRealworksKenmerken: data.syncedRealworksKenmerken,
            excludedRealworksKenmerken: data.excludedRealworksKenmerken,
            syncedRealworksRelatietypes: data.syncedRealworksRelatietypes,
            syncedRealworksStatus: data.syncedRealworksStatus,
            updatedFrom: data.updatedFrom,
            updatedUntil: data.updatedUntil,
            apiUrl: data.apiUrl,
            apiKey: data.apiKey,
            eventTrackingAccountId: data.eventTrackingAccountId,
            eventTrackingKey: data.eventTrackingKey,
            defaultTeams: data.defaultTeams,
            defaultStatus: data.defaultStatus,
            emmaDefaults: data.emmaDefaults || {},
            syncedRealworksAgendatypes: data.syncedRealworksAgendatypes,
            syncedRealworksTaaktypes: data.syncedRealworksTaaktypes,
            enabled: data.enabled,
            user: data.updatedBy instanceof User ? data.updatedBy.resourceIdentifier : null,
        })
    }

    async updateMailchimpCredentials(id: string, data: MailchimpCredentials): Promise<void> {
        const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/mailchimp_credentials`, {
            includeInternalContacts: data.includeInternalContacts,
            includeExternalContacts: data.includeExternalContacts,
            includeRealworksRelaties: data.includeRealworksRelaties,
            syncedRealworksKenmerken: data.syncedRealworksKenmerken,
            updatedFrom: data.updatedFrom,
            updatedUntil: data.updatedUntil,
            enabled: data.enabled,
            user: data.updatedBy instanceof User ? data.updatedBy.resourceIdentifier : null,
        })
    }

    async updateTypeformCredentials(id: string, data: TypeformCredentials): Promise<TypeformCredentials> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}/typeform_credentials`, {
                apiKey: data.apiKey,
                enabled: data.enabled,
                firstViewingAgendaTypes: data.firstViewingAgendaTypes,
                secondViewingAgendaTypes: data.secondViewingAgendaTypes,
                user: data.updatedBy instanceof User ? data.updatedBy.resourceIdentifier : null,
            })
            return data
        } catch (error) {
            this.handleError(error)
        }
    }

    async getOrderSettings(companyId: string): Promise<OrderSettings> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${companyId}/order_settings`)
            return OrderSettings.fromData(response.data)

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateOrderSettings(companyId: string, orderSettings: OrderSettings): Promise<OrderSettings> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${companyId}/order_settings`, orderSettings)
            return await this.getOrderSettings(companyId)

        } catch (error) {
            this.handleError(error)
        }
    }

    async getAutoCloseSettings(companyId: string): Promise<AutoCloseSettings> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${companyId}/auto_close_settings`)
            return AutoCloseSettings.fromData(response.data)

        } catch (error) {
            this.handleError(error)
        }
    }

    async updateAutoCloseSettings(companyId: string, autoCloseSettings: AutoCloseSettings): Promise<AutoCloseSettings> {
        try {
            await this.client.put(`${this.basePath}/${this.collectionName}/${companyId}/auto_close_settings`, autoCloseSettings)
            return await this.getAutoCloseSettings(companyId)

        } catch (error) {
            this.handleError(error)
        }
    }

    async updatePipelinesAllTab(company: Company): Promise<void> {
            await this.client.put(`${this.basePath}/${this.collectionName}`, {
            pipelinePhaseShowAllTab: company.pipelinePhaseShowAllTab,
        })
    }
}
